import React, { useEffect, useState } from 'react';
import { Link, Navigate, useLocation, useNavigate } from 'react-router-dom';
import api, { logout, storeSession } from '../../utils/authUtils';
import { STAFF_ROLES } from '../../utils/staffRoles';
import { PermissionsContext } from './permissions';

// The route guards (P13), wired in since the switch (P15). The roles are the ones
// authority.AuthorityService computes: ADMIN or TEAM (accounts.role, V70), MEMBER (every account) and
// SPEAKER (a person who was asked to a program assignment, N4). A guard lets in any of its `roles`.
//
// Contract with the backend (P12): GET /api/me answers 200 with at least
//   { "roles": ["MEMBER", "SPEAKER"] }     role names without the ROLE_ prefix (AuthorityService.rolesOf)
//   "permissions": ["staff.area", ...]     the keys of authority.Permission the roles hold; the guard
//                                          provides them to the screens below it (./permissions)
// and 401/403 for a missing, expired or pre-release token.
//
// Only a refused login means "logged out", and the two kinds are told apart (utils/authUtils):
//   - a session that existed and ended (the tokens expired, or the refresh was refused): the response
//     interceptor answers 'Session expired. Please login again.' and the guard goes to the login page
//     with ?session_expired=true, which shows that message;
//   - a visitor who never logged in (no tokens at all): /api/me answers a plain 401 and the guard goes
//     to the login page without any message, as before the switch.
// Both kinds clear the cached role as well, so the navbar stops showing a logged in person, and carry
// the page asked for in ?redirect= so the login returns there (the login pages honour only a path
// of this site, utils/redirectUtils).
// Every other failure - the request never reached the server (network error, timeout, aborted), or
// the server answered 403/5xx - is not the person's fault and must not look like a logged out
// browser: it shows the error block with a Retry instead.
//
// The guard never trusts the roles cached in localStorage by an earlier login: that cache is one
// refresh behind (POST /api/refresh-token carries no roles) and a browser that logged in before the
// switch still holds the retired ATTENDEE / SPEAKER names, so it would let the wrong people in and
// lock the right ones out. On every mount the guard asks /api/me, decides on that answer, and then
// replaces the cache with it.

const RoleRoute = ({ roles: allowed, loginPath = '/login', deniedPath = '/', children }) => {
  const [status, setStatus] = useState('checking');
  const [permissions, setPermissions] = useState([]);
  // Retry re-runs the check effect by bumping this, so a failed request can be asked again without a
  // full page reload.
  const [attempt, setAttempt] = useState(0);
  const navigate = useNavigate();
  const location = useLocation();
  const redirect = `redirect=${encodeURIComponent(location.pathname + location.search)}`;

  useEffect(() => {
    let cancelled = false;
    const check = async () => {
      try {
        const { data } = await api.get('/api/me');
        const roles = Array.isArray(data && data.roles) ? data.roles : [];
        storeSession({ roles });
        if (!cancelled) {
          setPermissions(Array.isArray(data && data.permissions) ? data.permissions : []);
          setStatus(roles.some((role) => allowed.includes(role)) ? 'allowed' : 'denied');
        }
      } catch (error) {
        if (cancelled) return;
        // Only a refused login is "logged out" (see the header): the interceptor's expiry message, or a
        // 401. Anything else is a failure to ask, not an answer of "no".
        if (error && error.message === 'Session expired. Please login again.') {
          navigate(`${loginPath}?session_expired=true&${redirect}`, { replace: true });
        } else if (error && error.response && error.response.status === 401) {
          logout();
          setStatus('anonymous');
        } else {
          setStatus('error');
        }
      }
    };
    check();
    return () => { cancelled = true; };
  }, [allowed, loginPath, navigate, redirect, attempt]);

  if (status === 'checking') return <div>Loading...</div>;
  if (status === 'anonymous') return <Navigate to={`${loginPath}?${redirect}`} replace />;
  if (status === 'denied') return <Navigate to={deniedPath} replace />;
  if (status === 'error') {
    return (
      <div>
        <p>Could not check your access. Please try again.</p>
        <button
          type="button"
          onClick={() => { setStatus('checking'); setAttempt((n) => n + 1); }}
        >
          Retry
        </button>
        <p><Link to={loginPath}>Log in</Link></p>
      </div>
    );
  }
  return <PermissionsContext.Provider value={permissions}>{children}</PermissionsContext.Provider>;
};

const MEMBER = ['MEMBER'];
const SPEAKER = ['SPEAKER'];

export const MemberRoute = (props) => <RoleRoute roles={MEMBER} {...props} />;

export const SpeakerRoute = (props) => <RoleRoute roles={SPEAKER} {...props} />;

// The admin area (admins and the team) has its own login page, so it keeps its own redirect; a
// logged-in member who is not staff lands on the homepage instead of the login form.
export const AdminRoute = (props) => <RoleRoute roles={STAFF_ROLES} loginPath="/admin/login" {...props} />;

export default RoleRoute;
